/**
 * Admin dashboard component for DEX UI.
 * Main view for admin role - list parties, create new parties, delete parties, and manage API keys.
 * Composes party table, details, and modals for admin party management.
 */

import {
  listParties,
  createParty,
  deleteParty,
  listPartyKeys,
  createPartyKey,
  revokePartyKey
} from '../../api/admin.js';

import {
  setAdminField,
  setAdminStatus,
  selectParty,
  clearSelectedParty,
  setDeleteConfirming,
  clearDeleteConfirming,
  toggleSort,
  clearAdminState
} from '../../state/admin.js';

import { getState, subscribe } from '../../state/store.js';
import { renderPartyTable, initPartyTable } from './party-table.js';
import { renderPartyDetails, initPartyDetails } from './party-details.js';
import {
  showCreatePartyModal,
  hideCreatePartyModal
} from './create-party-modal.js';
import {
  showCreateKeyModal,
  hideCreateKeyModal
} from './create-key-modal.js';
import { showToast } from './toast.js';
// Note: keygen modal integration for reader creation is handled internally by create-party-modal.js

/**
 * Render the admin dashboard into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Dashboard state from store
 */
export function renderAdminDashboard(container, state = {}) {
  const {
    parties = [],
    selectedParty = null,
    selectedPartyKeys = [],
    sortColumn = 'party_id',
    sortDirection = 'asc',
    status = 'idle',
    error = null,
    deleteConfirming = null
  } = state.admin || {};

  // Build key counts map from loaded keys
  const partyKeyCounts = {};
  if (selectedParty && selectedPartyKeys.length >= 0) {
    partyKeyCounts[selectedParty.party_id] = selectedPartyKeys.length;
  }

  // Error banner
  const errorHtml = error ? `
    <div class="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
      <p class="text-sm text-red-800">${escapeHtml(error)}</p>
    </div>
  ` : '';

  container.innerHTML = `
    <div class="admin-dashboard">
      <!-- Header with Create Party button -->
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-900">Manage Parties</h2>
        <button
          id="admin-create-party-btn"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          ${status === 'loading' || status === 'creating' ? 'disabled' : ''}
        >
          + Create Party
        </button>
      </div>

      ${errorHtml}

      <!-- Party Table Container -->
      <div id="admin-party-table-container"></div>

      <!-- Create Party Modal Container -->
      <div id="admin-create-party-modal-container"></div>

      <!-- Create Key Modal Container -->
      <div id="admin-create-key-modal-container"></div>
    </div>
  `;

  // Render party table
  const tableContainer = container.querySelector('#admin-party-table-container');
  if (tableContainer) {
    renderPartyTable(tableContainer, {
      parties,
      partyKeyCounts,
      sortColumn,
      sortDirection,
      expandedPartyId: selectedParty?.party_id || null,
      deleteConfirming,
      loading: status === 'loading'
    });

    // Render party details inside expansion row if party is selected
    if (selectedParty) {
      const detailsContainer = tableContainer.querySelector(`#party-details-${selectedParty.party_id}`);
      if (detailsContainer) {
        const currentPartyId = state.session?.partyId || null;
        renderPartyDetails(detailsContainer, {
          party: selectedParty,
          keys: selectedPartyKeys,
          keysLoading: status === 'loading',
          currentPartyId
        });
      }
    }
  }
}

/**
 * Initialize the admin dashboard with event handlers.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function that removes event listeners and unsubscribes from store
 */
export function initAdminDashboard(container) {
  const cleanupFunctions = [];
  let tableCleanup = null;
  let detailsCleanup = null;
  let createPartyModalCleanup = null;
  let createKeyModalCleanup = null;

  // Get current state
  const state = getState();

  // Render initial UI
  renderAdminDashboard(container, state);

  // Initialize party table with callbacks
  function initTable() {
    const tableContainer = container.querySelector('#admin-party-table-container');
    if (!tableContainer) return;

    // Clean up previous table init
    if (tableCleanup) {
      tableCleanup();
      tableCleanup = null;
    }

    tableCleanup = initPartyTable(tableContainer, {
      onSort: (column) => {
        toggleSort(column);
      },

      onExpand: async (partyId) => {
        const currentState = getState();
        const currentExpanded = currentState.admin?.selectedParty?.party_id;

        // Toggle: if already expanded, collapse
        if (currentExpanded === partyId) {
          clearSelectedParty();
          return;
        }

        // Expand: select party and load its keys
        const party = currentState.admin?.parties?.find(p => p.party_id === partyId);
        if (party) {
          selectParty(party);
          await loadPartyKeys(partyId);
        }
      },

      onDelete: (partyId) => {
        setDeleteConfirming(partyId);
      },

      onConfirmDelete: async (partyId) => {
        try {
          setAdminStatus('deleting');
          await deleteParty(partyId);
          clearDeleteConfirming();

          // If this was the selected party, clear selection
          const currentState = getState();
          if (currentState.admin?.selectedParty?.party_id === partyId) {
            clearSelectedParty();
          }

          // Refresh party list
          await loadParties();
          showToast(`Party "${partyId}" deleted successfully`, 'success');
        } catch (err) {
          setAdminStatus('idle', err.message || 'Failed to delete party');
          showToast(err.message || 'Failed to delete party', 'error');
        }
      },

      onCancelDelete: () => {
        clearDeleteConfirming();
      }
    });
  }

  // Initialize party details with callbacks
  function initDetails() {
    const currentState = getState();
    const selectedParty = currentState.admin?.selectedParty;

    if (!selectedParty) {
      if (detailsCleanup) {
        detailsCleanup();
        detailsCleanup = null;
      }
      return;
    }

    const tableContainer = container.querySelector('#admin-party-table-container');
    const detailsContainer = tableContainer?.querySelector(`#party-details-${selectedParty.party_id}`);
    if (!detailsContainer) return;

    // Clean up previous details init
    if (detailsCleanup) {
      detailsCleanup();
      detailsCleanup = null;
    }

    detailsCleanup = initPartyDetails(detailsContainer, {
      onCreateKey: async (partyId) => {
        try {
          setAdminStatus('creating');
          const result = await createPartyKey(partyId);

          // Show the key modal with the new API key
          // Note: Don't call setAdminStatus or loadPartyKeys here - they trigger re-renders
          // that destroy the modal. Do it in onClose instead.
          const keyModalContainer = container.querySelector('#admin-create-key-modal-container');
          if (keyModalContainer) {
            const freshState = getState();
            const party = freshState.admin?.parties?.find(p => p.party_id === partyId);
            createKeyModalCleanup = showCreateKeyModal(keyModalContainer, {
              keyId: result.key_id,
              apiKey: result.api_key,
              partyId: partyId,
              role: party?.role || ''
            }, {
              onClose: async () => {
                hideCreateKeyModal(keyModalContainer);
                createKeyModalCleanup = null;
                // Refresh keys and reset status after modal closes
                setAdminStatus('idle');
                await loadPartyKeys(partyId);
              }
            });
          }

          showToast('API key created successfully', 'success');
        } catch (err) {
          setAdminStatus('idle', err.message || 'Failed to create API key');
          showToast(err.message || 'Failed to create API key', 'error');
        }
      },

      onRevokeKey: async (partyId, keyId) => {
        // Confirm dialog
        const confirmed = window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.');
        if (!confirmed) return;

        try {
          setAdminStatus('deleting');
          await revokePartyKey(partyId, keyId);
          setAdminStatus('idle');

          // Refresh keys for the party
          await loadPartyKeys(partyId);
          showToast('API key revoked successfully', 'success');
        } catch (err) {
          setAdminStatus('idle', err.message || 'Failed to revoke API key');
          showToast(err.message || 'Failed to revoke API key', 'error');
        }
      },

      onCopyPublicKey: async (publicKey) => {
        try {
          await navigator.clipboard.writeText(publicKey);
          showToast('Public key copied to clipboard!', 'success');
        } catch (err) {
          // Fallback: create textarea and select
          const textarea = document.createElement('textarea');
          textarea.value = publicKey;
          document.body.appendChild(textarea);
          textarea.select();
          try {
            document.execCommand('copy');
            showToast('Public key copied to clipboard!', 'success');
          } catch (fallbackErr) {
            showToast('Failed to copy. Please select and copy manually.', 'error');
          }
          document.body.removeChild(textarea);
        }
      }
    });
  }

  // Load parties from API
  async function loadParties() {
    try {
      setAdminStatus('loading');
      const result = await listParties();
      setAdminField('parties', result.parties || []);
      setAdminStatus('idle');
    } catch (err) {
      setAdminStatus('idle', err.message || 'Failed to load parties');
      showToast(err.message || 'Failed to load parties', 'error');
    }
  }

  // Load keys for a specific party
  async function loadPartyKeys(partyId) {
    try {
      const result = await listPartyKeys(partyId);
      setAdminField('selectedPartyKeys', result.keys || []);
    } catch (err) {
      showToast(err.message || 'Failed to load API keys', 'error');
    }
  }

  // Create party button click handler (defined once for re-use)
  function handleCreatePartyClick() {
    const modalContainer = container.querySelector('#admin-create-party-modal-container');
    if (modalContainer) {
      // Show create party modal with writer/reader role options
      // The modal handles keygen internally when creating readers
      createPartyModalCleanup = showCreatePartyModal(modalContainer, ['writer', 'reader'], {
        onClose: () => {
          hideCreatePartyModal(modalContainer);
          createPartyModalCleanup = null;
        },

        onRoleChange: (role) => {
          // Role change is handled internally by create-party-modal
          // When role changes to 'reader', public key field appears with keygen button
        },

        onSubmit: async (data) => {
          try {
            const result = await createParty(data);

            // Hide create party modal
            hideCreatePartyModal(modalContainer);
            createPartyModalCleanup = null;

            // Show key modal with the new API key
            const keyModalContainer = container.querySelector('#admin-create-key-modal-container');
            if (keyModalContainer && result.api_key) {
              createKeyModalCleanup = showCreateKeyModal(keyModalContainer, {
                keyId: result.key_id,
                apiKey: result.api_key,
                partyId: result.party_id,
                role: result.role
              }, {
                onClose: () => {
                  hideCreateKeyModal(keyModalContainer);
                  createKeyModalCleanup = null;
                }
              });
            }

            // Refresh party list
            await loadParties();
            showToast(`Party "${data.party_id}" created successfully`, 'success');
          } catch (err) {
            showToast(err.message || 'Failed to create party', 'error');
          }
        },

        onGenerateKeypair: () => {
          // Keygen modal is handled internally by create-party-modal
          // Flow: admin clicks "Generate new keypair" -> keygen modal opens
          // -> user generates keypair (downloads private key) -> public key auto-fills form
        }
      });
    }
  }

  // Attach create party button handler
  const createPartyBtn = container.querySelector('#admin-create-party-btn');
  if (createPartyBtn) {
    createPartyBtn.addEventListener('click', handleCreatePartyClick);
    cleanupFunctions.push(() => createPartyBtn.removeEventListener('click', handleCreatePartyClick));
  }

  // Subscribe to state changes
  const unsubscribe = subscribe((newState, changedPath) => {
    // Only re-render for admin state changes
    if (changedPath && (changedPath.includes('admin') || changedPath === '*')) {
      renderAdminDashboard(container, newState);
      initTable();
      initDetails();

      // Re-attach create party button handler after re-render
      const newCreatePartyBtn = container.querySelector('#admin-create-party-btn');
      if (newCreatePartyBtn) {
        newCreatePartyBtn.addEventListener('click', handleCreatePartyClick);
      }
    }
  });
  cleanupFunctions.push(unsubscribe);

  // Initialize table and details
  initTable();
  initDetails();

  // Load parties on mount
  loadParties();

  // Return cleanup function
  return function cleanup() {
    // Clean up event listeners
    cleanupFunctions.forEach(fn => fn());

    // Clean up child components
    if (tableCleanup) {
      tableCleanup();
      tableCleanup = null;
    }
    if (detailsCleanup) {
      detailsCleanup();
      detailsCleanup = null;
    }
    if (createPartyModalCleanup) {
      createPartyModalCleanup();
      createPartyModalCleanup = null;
    }
    if (createKeyModalCleanup) {
      createKeyModalCleanup();
      createKeyModalCleanup = null;
    }

    // Clear admin state on cleanup (e.g., logout or role switch)
    clearAdminState();
  };
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
